import { Device } from "@fairfleet/geotab";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MAINTENANCE_PRIORITY,
  MAINTENANCE_PRIORITY_LABEL_KEYS,
  MaintenancePriorityCode,
} from "@/constants/MaintenancePriority";
import { useCreateMaintenanceWorkOrder } from "@/hooks/maintenance/useCreateMaintenanceWorkOrder";
import Warning, { WarningType } from "@/models/Warning";
import RulesAddinData from "@/pages/pmrules-edit/models/RulesAddinData";

export interface CreateMaintenanceOrderDialogProps {
  open: boolean;
  device: Device;
  warning: Warning;
  ruleAddinData: RulesAddinData;
  onClose: () => void;
}

const PRIORITY_OPTIONS: MaintenancePriorityCode[] = [
  MAINTENANCE_PRIORITY.LOW,
  MAINTENANCE_PRIORITY.MEDIUM,
  MAINTENANCE_PRIORITY.HIGH,
  MAINTENANCE_PRIORITY.CRITICAL,
];

export function CreateMaintenanceOrderDialog(props: CreateMaintenanceOrderDialogProps) {
  const { open, device, warning, ruleAddinData, onClose } = props;
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync, isPending } = useCreateMaintenanceWorkOrder();

  const [priorityCode, setPriorityCode] = useState<MaintenancePriorityCode>(
    MAINTENANCE_PRIORITY.MEDIUM,
  );
  const [description, setDescription] = useState(ruleAddinData.pmDescription ?? "");
  const [notes, setNotes] = useState("");

  const handleSubmit = async () => {
    try {
      await mutateAsync({
        device,
        ruleId: ruleAddinData.ruleId,
        pmTitle: ruleAddinData.pmTitle,
        pmDescription: ruleAddinData.pmDescription,
        warningType: warning.type as WarningType,
        priorityCode,
        description,
        notes,
      });
      enqueueSnackbar(t("createPmOrder.success"), { variant: "success" });
      onClose();
    } catch (err) {
      const stage = (err as { stage?: string } | null)?.stage;
      if (stage === "job") {
        enqueueSnackbar(t("createPmOrder.errorJob"), { variant: "warning" });
        onClose();
      } else {
        enqueueSnackbar(t("createPmOrder.errorOrder"), { variant: "error" });
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("createPmOrder.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {t("createPmOrder.device")}:
            </Typography>
            <Typography variant="body2">{device.name}</Typography>
          </Stack>
          <TextField
            label={t("createPmOrder.description")}
            multiline
            rows={3}
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
          <TextField
            select
            label={t("createPmOrder.priority")}
            value={priorityCode}
            onChange={e => setPriorityCode(Number(e.target.value) as MaintenancePriorityCode)}
          >
            {PRIORITY_OPTIONS.map(code => (
              <MenuItem key={code} value={code}>
                {t(MAINTENANCE_PRIORITY_LABEL_KEYS[code])}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t("createPmOrder.notes")}
            multiline
            rows={2}
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>
          {t("createPmOrder.cancel")}
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isPending}>
          {t("createPmOrder.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
